import 'babel-polyfill';
import 'react-app-polyfill/ie11';

import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { Router, Route, Switch, useHistory } from 'react-router-dom';
import { history } from 'nallian-shared-ui/lib/config/history';
import configureStore from 'nallian-shared-ui/lib/store/configureStore';
import { basename } from 'nallian-shared-ui/lib/lib/path';
import { getApplicationInsights } from 'nallian-shared-ui/lib/config/applicationInsights';
import Layout from 'nallian-shared-ui/lib/layout';
import 'nallian-shared-ui/lib/config/i18n';
import { createMsalInstance } from 'nallian-shared-ui/lib/authentication/helpers/createMsalInstance';
import { msalConfig } from 'nallian-shared-ui/lib/authentication/constants/config';
import { MsalProvider } from '@azure/msal-react';
import MsalToken from 'nallian-shared-ui/lib/authentication/MsalToken';
import Logout from 'nallian-shared-ui/lib/authentication/Logout';
import routes from './app/config/routes';
import translations from './app/config/translations';
import rootReducer from './rootReducer';
import rootSaga from './rootSaga';
import RequestToJoin from './portal/joinRequest/components/RequestToJoin';
import LandingPage from './portal/landing/components/LandingPage';
import NotFoundPage from './portal/404/components/NotFoundPage';
import { getLayoutRoutes } from './portal/app/helpers/routesHelper';
import WithTranslations from './portal/withTranslations';
import Invitation from './portal/onboarding/components/Invitation';
import CompleteInvitation from './portal/onboarding/components/CompleteInvitation';
import { GlobalStyle } from './mgmt/app/styles'; // For IE 11 support

// Function to load the dynamic script
const loadDynamicScript = () => {
  return new Promise((resolve, reject) => {
    const url = window.location.hostname;
    const formattedUrl = url.replace(/\./g, '_');
    const srcUrl = `${window.location.origin}${basename}/_configs/${formattedUrl}.js`;
    const script = document.createElement('script');
    script.src = srcUrl;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const init = async () => {
  const insights = getApplicationInsights();

  if (insights)
    insights.trackTrace({
      message: `client-id before loading dynamic script: ${window.appSettings.clientid}`,
      severityLevel: SeverityLevel.Information,
    });

  await loadDynamicScript();

  if (insights)
    insights.trackTrace({
      message: `client-id after loading dynamic script: ${window.appSettings.clientid}`,
      severityLevel: SeverityLevel.Information,
    });

  const store = configureStore(rootReducer, rootSaga);

  const customConfig = {
    ...msalConfig,
    auth: {
      ...msalConfig.auth,
      clientId: window.appSettings.clientid,
      redirectUri: `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? `:${window.location.port}` : ''
      }${basename}/signIncallback`,
    },
  };

  const msalInstance = createMsalInstance(customConfig);

  const otherRoutes = [...getLayoutRoutes(), '/signInCallback', '/logout'].filter(r => r);
  const otherRoutesWithJoin = [
    ...getLayoutRoutes(),
    '/signInCallback',
    '/logout',
    '/complete-invitation',
  ].filter(r => r);

  const DebugRouter = ({ children }) => {
    const { location } = useHistory();
    if (process.env.NODE_ENV === 'development') {
      console.log(
        `Route: ${location.pathname}${location.search}, State: ${JSON.stringify(location.state)}`
      );
    }

    return children;
  };

  ReactDOM.render(
    <Provider store={store}>
      <Router history={history} basename={basename}>
        <WithTranslations translations={translations}>
          <MsalProvider instance={msalInstance}>
            <Switch>
              <Route path="/" component={LandingPage} exact />
              <Route path="/request-to-join" component={RequestToJoin} exact />
              <Route path="/invite/:token" component={Invitation} exact />
              <Route path="/sign-out" component={Logout} exact />
              <Route
                path={otherRoutesWithJoin}
                render={() => {
                  return (
                    <MsalToken>
                      <DebugRouter>
                        <Switch>
                          <Route
                            path="/complete-invitation/:token"
                            component={CompleteInvitation}
                            exact
                          />
                          <Route
                            path={otherRoutes}
                            render={() => (
                              <>
                                <GlobalStyle />
                                <Layout
                                  routes={routes}
                                  applicationName="Nallian Portal"
                                  translations={translations}
                                  showUserMenuApps
                                />
                              </>
                            )}
                          />
                        </Switch>
                      </DebugRouter>
                    </MsalToken>
                  );
                }}
              />
              <Route path="/" component={NotFoundPage} />
            </Switch>
          </MsalProvider>
        </WithTranslations>
      </Router>
    </Provider>,
    document.getElementById('root')
  );
};

init();
